import type { CameraConfig, GlanceShape, ShowcaseShape } from '../config/camera.config';
import { clamp, damp, smoothDamp, smoothstep, type SmoothDampState } from '../utils/math';

/** Accumulated per-frame adjustments applied on top of the follow rig. */
export interface CameraAdjustments {
  /** Offsets in the camera rig's local frame: x right, y up, z forward (meters). */
  offsetX: number;
  offsetY: number;
  offsetZ: number;
  /** Vertical offset of the look-at target (meters) – positive looks up. */
  targetOffsetY: number;
  rollDegrees: number;
  /** Absolute field of view (degrees). */
  fovDegrees: number;
  yawOffsetDegrees: number;
  /**
   * 0..1: the rig pivots on the runner's exact lane position (instead of the partially followed one) and
   * looks at the runner instead of ahead – an orbit that keeps the runner in the middle of the picture.
   */
  focusRunner: number;
}

export interface CameraModifierContext {
  /** 0 at start speed, 1 at max speed. */
  readonly normalizedSpeed: number;
  /** Render time (seconds) – stands still while the game is paused. */
  readonly time: number;
  readonly sliding: boolean;
  /** 0 on the open site … 1 inside a tunnel, interior or shell at the camera (walls close by). */
  readonly enclosed: number;
  /** The runner rides a vehicle (dozer set piece) – see DriveCameraModifier. */
  readonly driving?: boolean;
  /** The vehicle is the ructec-Bulli, and its speed share 0..1 (the picture widens with it). */
  readonly drivingBus?: boolean;
  readonly busSpeedShare?: number;
}

export interface CameraModifier {
  update(dt: number, ctx: CameraModifierContext, out: CameraAdjustments): void;
  reset(): void;
}

/** Widens the FOV with speed – a strong but cheap speed cue. */
export class SpeedFovModifier implements CameraModifier {
  private fov: number;

  constructor(private readonly config: CameraConfig['fov']) {
    this.fov = config.baseDegrees;
  }

  update(dt: number, ctx: CameraModifierContext, out: CameraAdjustments): void {
    const target = this.config.baseDegrees + (this.config.maxDegrees - this.config.baseDegrees) * ctx.normalizedSpeed;
    this.fov = damp(this.fov, target, this.config.damping, dt);
    out.fovDegrees = this.fov;
  }

  reset(): void {
    this.fov = this.config.baseDegrees;
  }
}

/** Trauma-based shake: trauma decays linearly, intensity = trauma². */
export class ShakeModifier implements CameraModifier {
  private trauma = 0;

  constructor(private readonly config: CameraConfig['shake']) {}

  addTrauma(amount: number): void {
    this.trauma = clamp(this.trauma + amount, 0, 1);
  }

  update(dt: number, ctx: CameraModifierContext, out: CameraAdjustments): void {
    if (this.trauma <= 0) return;
    const intensity = this.trauma * this.trauma;
    const t = ctx.time * this.config.frequency;
    // Sum of incommensurate sines ≈ smooth noise without a noise library.
    out.offsetX += (Math.sin(t * 1.0) + Math.sin(t * 2.3) * 0.5) * 0.66 * this.config.maxOffset * intensity;
    out.offsetY += (Math.sin(t * 1.7 + 1.3) + Math.sin(t * 3.1) * 0.5) * 0.66 * this.config.maxOffset * intensity;
    out.rollDegrees += Math.sin(t * 1.3 + 0.7) * this.config.maxRollDegrees * intensity;
    this.trauma = Math.max(0, this.trauma - this.config.decay * dt);
  }

  reset(): void {
    this.trauma = 0;
  }
}

/** Short spring dip when the player lands. */
export class LandingDipModifier implements CameraModifier {
  private offset = 0;
  private velocity = 0;

  constructor(private readonly config: CameraConfig['landing']) {}

  land(impactSpeed: number): void {
    const dip = Math.min(impactSpeed * this.config.dipPerImpactSpeed, this.config.maxDip);
    this.velocity -= dip * Math.sqrt(this.config.stiffness);
  }

  update(dt: number, _ctx: CameraModifierContext, out: CameraAdjustments): void {
    const accel = -this.config.stiffness * this.offset - this.config.damping * this.velocity;
    this.velocity += accel * dt;
    this.offset += this.velocity * dt;
    out.offsetY += this.offset;
  }

  reset(): void {
    this.offset = 0;
    this.velocity = 0;
  }
}

/**
 * Drops the camera and tilts it up while sliding, so overhead obstacles do not
 * hide what comes next. Moves along a critically damped spring – no jumps
 * when the slide starts or the player gets up again.
 */
export class SlideCameraModifier implements CameraModifier {
  private weight = 0;
  private readonly spring: SmoothDampState = { velocity: 0 };

  constructor(private readonly config: CameraConfig['slide']) {}

  update(dt: number, ctx: CameraModifierContext, out: CameraAdjustments): void {
    const time = ctx.sliding ? this.config.blendInTime : this.config.blendOutTime;
    this.weight = clamp(smoothDamp(this.weight, ctx.sliding ? 1 : 0, this.spring, time, dt), 0, 1);
    out.offsetY += this.config.heightOffset * this.weight;
    out.offsetZ += this.config.closerBy * this.weight;
    // The look target must not drop with the camera: compensate the drop and add the look-up offset.
    out.targetOffsetY += (this.config.lookUpOffset - this.config.heightOffset) * this.weight;
  }

  reset(): void {
    this.weight = 0;
    this.spring.velocity = 0;
  }
}

/**
 * Riding the dozer: the camera rises, moves back and looks a little higher – on a spring, so mounting and jumping
 * off never jolt the picture (like the slide view).
 */
export class DriveCameraModifier implements CameraModifier {
  private weight = 0;
  private readonly spring: SmoothDampState = { velocity: 0 };

  constructor(private readonly config: CameraConfig['drive']) {}

  update(dt: number, ctx: CameraModifierContext, out: CameraAdjustments): void {
    const driving = ctx.driving === true;
    const time = driving ? this.config.blendInTime : this.config.blendOutTime;
    this.weight = clamp(smoothDamp(this.weight, driving ? 1 : 0, this.spring, time, dt), 0, 1);
    // the profile of the vehicle ridden last stays while blending out (no jump to the dozer values after the van)
    if (driving) this.bus = ctx.drivingBus === true;
    const profile = this.bus ? this.config.bus : this.config;
    out.offsetY += profile.heightOffset * this.weight;
    out.offsetZ += profile.closerBy * this.weight;
    out.targetOffsetY += profile.lookUpOffset * this.weight;
    if (this.bus) {
      this.fov = smoothDamp(this.fov, (ctx.busSpeedShare ?? 0) * this.config.bus.fovAtSpeed, this.fovSpring, 0.6, dt);
      out.fovDegrees += this.fov * this.weight;
    }
  }

  private bus = false;
  private fov = 0;
  private readonly fovSpring: SmoothDampState = { velocity: 0 };

  reset(): void {
    this.weight = 0;
    this.spring.velocity = 0;
  }
}

/**
 * Prepared: yaws the camera towards an upcoming 90° turn. Needs turn
 * segments – until they exist the modifier stays neutral.
 */
export class TurnAnticipationModifier implements CameraModifier {
  private yaw = 0;
  private upcomingTurnSign = 0;
  private distanceToTurn = Infinity;

  constructor(private readonly config: CameraConfig['turnAnticipation']) {}

  setUpcomingTurn(sign: -1 | 0 | 1, distance: number): void {
    this.upcomingTurnSign = sign;
    this.distanceToTurn = distance;
  }

  update(dt: number, _ctx: CameraModifierContext, out: CameraAdjustments): void {
    let target = 0;
    if (this.config.enabled && this.upcomingTurnSign !== 0 && this.distanceToTurn < this.config.startDistance) {
      target = this.upcomingTurnSign * this.config.maxYawDegrees * (1 - this.distanceToTurn / this.config.startDistance);
    }
    this.yaw = damp(this.yaw, target, 4, dt);
    out.yawOffsetDegrees += this.yaw;
  }

  reset(): void {
    this.yaw = 0;
    this.upcomingTurnSign = 0;
    this.distanceToTurn = Infinity;
  }
}

/** Total length of a glance (seconds). */
export function glanceDuration(shape: GlanceShape): number {
  return shape.attack + shape.hold + shape.release;
}

/** Glance weight 0..1 at `elapsed` seconds – eased in and out, 0 outside the envelope. */
export function glanceWeight(shape: GlanceShape, elapsed: number): number {
  if (elapsed < 0 || elapsed >= glanceDuration(shape)) return 0;
  if (elapsed < shape.attack) return smoothstep(elapsed / shape.attack);
  const releaseStart = shape.attack + shape.hold;
  return elapsed < releaseStart ? 1 : 1 - smoothstep((elapsed - releaseStart) / shape.release);
}

/**
 * Short orbit round the runner for a moment worth seeing (extra-life drink): yaw, closer, lower and a
 * look-at point lifted to the upper body, eased in and out like a glance. Fades out in enclosures
 * (tunnel walls). Uses render time, so it holds still while the game is paused.
 */
export class ShowcaseModifier implements CameraModifier {
  private shape: ShowcaseShape | null = null;
  private side = 0;
  private startTime: number | null = null;

  /** side: +1 ends up at the runner's front left, -1 at the front right. */
  start(side: -1 | 1, shape: ShowcaseShape): void {
    this.shape = shape;
    this.side = side;
    this.startTime = null;
  }

  update(_dt: number, ctx: CameraModifierContext, out: CameraAdjustments): void {
    if (!this.shape) return;
    this.startTime ??= ctx.time;
    const elapsed = ctx.time - this.startTime;
    if (elapsed >= glanceDuration(this.shape)) {
      this.reset();
      return;
    }
    const w = glanceWeight(this.shape, elapsed) * (1 - clamp(ctx.enclosed, 0, 1));
    out.yawOffsetDegrees += this.side * this.shape.yawDegrees * w;
    out.offsetZ += this.shape.closerBy * w;
    out.offsetY += this.shape.heightOffset * w;
    out.targetOffsetY += this.shape.targetLift * w;
    out.focusRunner = Math.max(out.focusRunner, w);
  }

  reset(): void {
    this.shape = null;
    this.side = 0;
    this.startTime = null;
  }
}
